import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, MessageCircle, BookOpen, ShieldCheck, Smartphone, Globe } from 'lucide-react';

/**
 * Public Support / Help page.
 * Route: /support
 * Required by Apple App Store as a public "Support URL".
 */
export default function Support() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className="bg-gradient-to-br from-orange-500 to-rose-600 text-white">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <Link to="/" className="inline-flex items-center gap-2 text-sm opacity-80 hover:opacity-100 mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Taptile
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold">Support</h1>
          <p className="mt-2 opacity-90 text-lg">
            Help, FAQs, and how to reach a human.
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-10">

        {/* Contact card */}
        <section className="bg-white rounded-2xl shadow-sm p-6 border border-slate-200">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-600" /> Get in touch
          </h2>
          <p className="mb-4">
            The fastest way to reach us is by email. We aim to respond within 24 hours on UK business days.
          </p>
          <a
            href="mailto:taptile@dynatexsaas.com"
            data-testid="support-email-link"
            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-colors"
          >
            <Mail className="w-4 h-4" /> taptile@dynatexsaas.com
          </a>
          <p className="text-xs text-slate-500 mt-3">
            Please include your organisation name, what you were trying to do, and any error messages or screenshots.
          </p>
        </section>

        {/* Quick links */}
        <section className="grid sm:grid-cols-2 gap-4">
          <QuickCard
            icon={<Smartphone className="w-5 h-5 text-blue-600" />}
            title="iOS app help"
            body="Push notifications, login issues, app updates, install instructions."
            anchor="#ios"
          />
          <QuickCard
            icon={<Globe className="w-5 h-5 text-emerald-600" />}
            title="Admin & web portal"
            body="Tiles, articles, design, notifications, contacts sync."
            anchor="#admin"
          />
          <QuickCard
            icon={<ShieldCheck className="w-5 h-5 text-purple-600" />}
            title="Privacy & data"
            body="What we collect, how we use it, your rights."
            anchor="#privacy"
          />
          <QuickCard
            icon={<BookOpen className="w-5 h-5 text-amber-600" />}
            title="Integrations"
            body="NexusCMS, Wonde, SSO, email, payments."
            anchor="#integrations"
          />
        </section>

        {/* FAQ — iOS */}
        <FAQ id="ios" title="iOS app — frequently asked questions">
          <Q q="Why am I not receiving push notifications?">
            <p>Check the following on your iPhone:</p>
            <ol className="list-decimal pl-6 space-y-1">
              <li>Settings &rarr; Notifications &rarr; Taptile &rarr; ensure &ldquo;Allow Notifications&rdquo; is on.</li>
              <li>Make sure you have an active internet connection.</li>
              <li>Sign out of the app and sign back in (this refreshes your push token).</li>
              <li>If still not working, email us with your device model and iOS version.</li>
            </ol>
          </Q>
          <Q q="How do I log in to the app?">
            <p>
              Open the Taptile app, enter your organisation&rsquo;s <strong>short code</strong> (e.g. <em>STPETERS</em>), then, if your organisation requires it,
              sign in with the email and password issued by your admin. Some organisations skip the login step.
            </p>
          </Q>
          <Q q="The app shows old content / old branding.">
            <p>Pull down from the top of the home screen to refresh. If that doesn’t work, force-quit the app and reopen.</p>
          </Q>
          <Q q="My account is locked or I forgot my password.">
            <p>Contact your school administrator first &mdash; they can reset your password instantly. If they cannot help, email us.</p>
          </Q>
        </FAQ>

        {/* FAQ — Admin */}
        <FAQ id="admin" title="Admin portal — frequently asked questions">
          <Q q="How do I add a tile?">
            <p>
              Log in to <strong>taptile.app</strong> &rarr; <strong>Tiles</strong> tab &rarr; <strong>Add Tile</strong>. Enter a
              title, choose an icon, and paste the URL the tile should open.
            </p>
          </Q>
          <Q q="How do I send a push notification?">
            <p>
              Log in &rarr; <strong>Notify</strong> tab &rarr; write title and body &rarr; choose recipients &rarr; <strong>Send</strong>.
              Members of your tenant with the app installed will receive it within seconds.
            </p>
          </Q>
          <Q q="How do I change my logo or colours?">
            <p>
              Log in &rarr; <strong>Design</strong> tab. Upload banner, app icon, and pick brand colours. Save and changes appear
              instantly in the web portal and (after pull-to-refresh) the iOS app.
            </p>
          </Q>
          <Q q="Can I import a list of contacts?">
            <p>
              Yes &mdash; admins can upload a CSV under <strong>Users</strong> tab. For ongoing automatic sync, use the NexusCMS or
              Wonde integration in the <strong>Connect</strong> tab.
            </p>
          </Q>
        </FAQ>

        {/* FAQ — Integrations */}
        <FAQ id="integrations" title="Integrations">
          <Q q="How do I connect NexusCMS?">
            <p>
              Log in &rarr; <strong>Connect</strong> tab &rarr; <strong>Generate</strong> API key &rarr; copy it immediately. Send
              the key (securely) and the integration spec to your NexusCMS administrator.
            </p>
          </Q>
          <Q q="How do I connect Wonde?">
            <p>
              Log in &rarr; <strong>Connect</strong> tab &rarr; <strong>Wonde Integration</strong>. Toggle on and paste your Wonde
              API token. Initial sync runs immediately, with daily automatic sync coming later in 2026.
            </p>
          </Q>
          <Q q="What payment methods do you accept?">
            <p>
              All major credit and debit cards via Stripe. Direct Debit and invoicing available on annual plans &mdash; email us.
            </p>
          </Q>
        </FAQ>

        {/* FAQ — Privacy */}
        <FAQ id="privacy" title="Privacy &amp; security">
          <Q q="Where is my data stored?">
            <p>
              Primary data is stored in our MongoDB Atlas EU region. Uploaded images are in DigitalOcean Spaces in Frankfurt.
              See our <Link to="/privacy" className="text-blue-600 underline">Privacy Policy</Link> for the full list of sub-processors.
            </p>
          </Q>
          <Q q="Can I delete all my data?">
            <p>
              Yes &mdash; email us and we will delete your account and all associated data within 30 days. Backups are then purged
              within a further 30 days.
            </p>
          </Q>
          <Q q="Are you GDPR-compliant?">
            <p>
              Yes. We follow UK and EU GDPR requirements, support all subject access rights, and never sell or share your data
              for advertising purposes.
            </p>
          </Q>
        </FAQ>

        {/* Bottom contact */}
        <section className="bg-white rounded-2xl shadow-sm p-6 border border-slate-200 text-center">
          <MessageCircle className="w-8 h-8 text-orange-600 mx-auto mb-3" />
          <h3 className="text-lg font-bold mb-2">Still stuck?</h3>
          <p className="text-sm text-slate-600 mb-4">
            We’re real people based in the UK. Email us with as much detail as you can and we’ll get back to you quickly.
          </p>
          <a
            href="mailto:taptile@dynatexsaas.com"
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-colors"
          >
            <Mail className="w-4 h-4" /> taptile@dynatexsaas.com
          </a>
        </section>

        <div className="pt-4 text-center text-sm text-slate-500">
          <Link to="/" className="text-blue-600 underline">&larr; Return to taptile.app</Link>
          {' \u00B7 '}
          <Link to="/privacy" className="text-blue-600 underline">Privacy Policy</Link>
          {' \u00B7 '}
          <Link to="/terms" className="text-blue-600 underline">Terms of Service</Link>
        </div>
      </main>
    </div>
  );
}

function QuickCard({ icon, title, body, anchor }) {
  return (
    <a href={anchor} className="block bg-white rounded-2xl border border-slate-200 p-5 hover:border-orange-400 hover:shadow-md transition-all">
      <div className="flex items-center gap-2 mb-1">{icon}<h3 className="font-semibold">{title}</h3></div>
      <p className="text-sm text-slate-600">{body}</p>
    </a>
  );
}

function FAQ({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-8">
      <h2 className="text-xl font-bold mb-4 text-slate-900 border-b-2 border-orange-500 pb-1">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Q({ q, children }) {
  return (
    <details className="bg-white rounded-lg border border-slate-200 p-4 group">
      <summary className="font-semibold cursor-pointer list-none flex items-start justify-between gap-2">
        <span>{q}</span>
        <span className="text-orange-600 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
      </summary>
      <div className="mt-3 text-sm text-slate-700 space-y-2">{children}</div>
    </details>
  );
}
